import React, { useEffect, useState, useMemo } from 'react'
import { Link } from 'react-router-dom';
import { postEquiposEnLaboratorio } from '../../api/common';
import { Alertassw } from '../Alertas/Alertassw';
import Spinner from '../commons/Spinner';
import { Container, Table, Row, Col, Form, Button, InputGroup, Badge, Pagination, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useAuth } from '../../hooks/useAuth';
import LoteCreateModal from '../mediciones/lotes/LoteCreateModal';
import capitalizeText from '../../utils/capitalizeText';

// Lista mejorada de equipos ingresados y creación de lotes.
// Prop opcional `onLoteCreado`: callback invocado tras crear un lote exitosamente
// (spec lotes-cola-lotes-refresh-reactivo — bumpea refreshKey en el padre para
// que LotesList se refresque sin F5 manual). Default no-op para renders standalone.
export const ColaIngresados = ({ onLoteCreado = () => {} }) => {
    const [equipos, setEquipos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [refresh, setRefresh] = useState(false);
    const { userData } = useAuth();

    // filtros y paginado
    const [search, setSearch] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [sortAsc, setSortAsc] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    // selección de equipos (IDs)
    const [selectedIds, setSelectedIds] = useState([]);

    // modal crear lote
    const [showCreateModal, setShowCreateModal] = useState(false);

    useEffect(() => {
        async function fetchEquipos() {
            try {
                setLoading(true);
                const res = await postEquiposEnLaboratorio();

                const payload = res && res.data ? (res.data.data ?? res.data) : res;

                
                setEquipos(Array.isArray(payload) ? payload : []);
            } catch (error) {
                console.error(error);
                Alertassw('error', 'Error al cargar equipos en laboratorio');
            } finally {
                setLoading(false);
            }
        }
        fetchEquipos();
    }, [refresh]);

    // filtrado y ordenamiento memorizar
    const filtered = useMemo(() => {
        const s = search.trim().toLowerCase();
        let list = equipos.slice();
        if (s) {
            list = list.filter(e => {
                const cliente = e?.equipo?.cliente?.nombre ?? "";
                const nombre = e?.equipo?.item?.nombre ?? "";
                const consecutivo = e?.consecutivo ?? "";
                return `${cliente} ${nombre} ${consecutivo}`.toLowerCase().includes(s);
            });
        }
        if (startDate) {
            const sd = new Date(startDate);
            list = list.filter(e => new Date(e.fechaIngreso) >= sd);
        }
        if (endDate) {
            const ed = new Date(endDate);
            // include whole day
            ed.setHours(23,59,59,999);
            list = list.filter(e => new Date(e.fechaIngreso) <= ed);
        }
        list.sort((a,b) => {
            const da = new Date(a.fechaIngreso);
            const db = new Date(b.fechaIngreso);
            return sortAsc ? da - db : db - da;
        });
        return list;
    }, [equipos, search, startDate, endDate, sortAsc]);

    // paginado
    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const currentPage = Math.min(page, totalPages);
    const pageItems = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    console.log('Equipos en laboratorio:', pageItems); // Verifica los datos recibidos del backend
    // Regla de selección: el equipo debe estar en estado 'Ingreso' Y no estar
    // ya en un lote (loteEnCurso null). Sirve para bloquear inclusión duplicada.
    const puedeSeleccionarse = (equipo) => equipo?.estado === 'Ingreso' && !equipo?.loteEnCurso;

    const toggleSelect = (id) => {
        const equipo = equipos.find(e => e._id === id);
        if (!puedeSeleccionarse(equipo)) return;
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const selectAllVisible = () => {
        const visibleIds = pageItems.filter(puedeSeleccionarse).map(i => i._id);
        const allSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.includes(id));
        if (allSelected) {
            setSelectedIds(prev => prev.filter(id => !visibleIds.includes(id)));
        } else {
            setSelectedIds(prev => Array.from(new Set([...prev, ...visibleIds])));
        }
    };

    const clearSelection = () => setSelectedIds([]);

    // funcion para estados de los equipos listados (ingresados, Cancelado, No Finalizado, Procesado, Pendiente, Aplazado etc)
    const getEstadoBadge = (estado) => {
        const map = {
            'Ingreso': 'primary',
            'Finalizado': 'success',
            'Cancelado': 'danger',
            'No Finalizado': 'warning',
            'Procesado': 'info',
            'Pendiente': 'secondary',
            'Aplazado': 'dark',
            'Aprobado': 'success'
        };
        return map[estado] || 'secondary';
    };

    // abrir modal crear lote
    const handleOpenCreate = () => {
        if (selectedIds.length === 0) return Alertassw('warning', 'Seleccione al menos un equipo');
        setShowCreateModal(true);
    };

    return (
        <div>
            <Container fluid>
                <Row className="align-items-center mb-3">
                    <Col>
                        <h4 className="bg-primary text-white p-2">Equipos en Laboratorio</h4>
                    </Col>
                    <Col className="text-end">
                        <Button variant="success" onClick={handleOpenCreate} disabled={selectedIds.length===0}>
                            Crear Lote de calibración ({selectedIds.length})
                        </Button>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col md={4} className="mb-2">
                        <InputGroup>
                            <Form.Control placeholder="Buscar cliente, equipo o consecutivo" value={search} onChange={(e)=>{setSearch(e.target.value); setPage(1);}} />
                        </InputGroup>
                    </Col>
                    <Col md={3} className="mb-2">
                        <Form.Control type="date" value={startDate} onChange={(e)=>{setStartDate(e.target.value); setPage(1);}} />
                    </Col>
                    <Col md={3} className="mb-2">
                        <Form.Control type="date" value={endDate} onChange={(e)=>{setEndDate(e.target.value); setPage(1);}} />
                    </Col>
                    <Col md={2} className="text-end">
                        <Button variant="outline-secondary" onClick={()=>setSortAsc(s=>!s)}>
                            Orden: {sortAsc? 'Asc' : 'Desc'}
                        </Button>
                    </Col>
                </Row>

                {loading ? <div className="text-center py-4"><Spinner /></div> : (
                    <>
                        {filtered.length === 0 ? (
                            <div className="alert alert-info">No hay equipos según los filtros.</div>
                        ) : (
                            <>
                                <Table responsive striped bordered hover>
                                    <thead>
                                        <tr>
                                            <th style={{padding: '0.5rem', textAlign: 'center', verticalAlign: 'middle', width: '40px'}}>
                                                <input type="checkbox" checked={pageItems.length > 0 && pageItems.filter(puedeSeleccionarse).every(i=>selectedIds.includes(i._id)) && pageItems.some(puedeSeleccionarse)} onChange={selectAllVisible} />
                                            </th>
                                            <th>Cliente</th>
                                            <th>Equipo / Item</th>
                                            <th>Serie</th>
                                            <th>Puntos</th>
                                            <th>Consecutivo</th>
                                            <th>Estado</th>
                                            <th>Lote</th>
                                            <th>Fecha Ingreso</th>
                                            <th>Ubicación</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pageItems.map(equipo => {
                                            const seleccionable = puedeSeleccionarse(equipo);
                                            const enLote = equipo?.loteEnCurso;
                                            return (
                                                <tr
                                                    key={equipo._id}
                                                    onClick={() => seleccionable && toggleSelect(equipo._id)}
                                                    style={{
                                                        cursor: seleccionable ? 'pointer' : 'not-allowed',
                                                        backgroundColor: selectedIds.includes(equipo._id) ? '#cfe2ff' : '',
                                                        opacity: seleccionable ? 1 : 0.6
                                                    }}
                                                >
                                                    <td
                                                        style={{padding: '0.5rem', textAlign: 'center', verticalAlign: 'middle'}}>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedIds.includes(equipo._id)}
                                                            onChange={()=>toggleSelect(equipo._id)}
                                                            onClick={(e) => e.stopPropagation()}
                                                            disabled={!seleccionable}
                                                            title={
                                                                enLote
                                                                    ? `Ya está en el lote ${enLote.numeroLote || enLote.consecutivo}. Anulá o finalizá ese lote para reincluirlo.`
                                                                    : equipo.estado !== 'Ingreso'
                                                                        ? `Estado ${equipo.estado} — no se puede incluir en un lote.`
                                                                        : 'Seleccionar'
                                                            }
                                                            style={{margin: 0}}
                                                            />
                                                    </td>
                                                    <td>{capitalizeText(equipo?.equipo?.cliente?.nombre)}</td>
                                                    <td>
                                                        <div><strong>{equipo?.snapshot?.item}</strong></div>
                                                        <div className="text-muted small">Marca: {equipo?.snapshot?.marca} • Modelo: {equipo?.snapshot?.modelo}</div>
                                                    </td>
                                                    <td>
                                                        <span className="small">{equipo?.snapshot?.serial ?? equipo?.serie ?? '—'}</span>
                                                    </td>
                                                    <td>{equipo?.snapshot?.puntos?? equipo?.snapshot?.puntos ?? 'N/A'}</td>
                                                    <td><Badge bg="dark">{equipo?.consecutivo}</Badge></td>
                                                    <td>
                                                        <Badge bg={getEstadoBadge(equipo?.estado)}>{equipo?.estado || 'Desconocido'}</Badge>
                                                    </td>
                                                    <td>
                                                        {enLote ? (
                                                            <OverlayTrigger
                                                                placement="top"
                                                                overlay={<Tooltip>Estado del lote: {enLote.estado}</Tooltip>}
                                                            >
                                                                <Link
                                                                    to={`/mediciones/lotes/${enLote.loteId}`}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    className="text-decoration-none"
                                                                >
                                                                    <Badge bg="info">
                                                                        {enLote.numeroLote || `#${enLote.consecutivo}`}
                                                                    </Badge>
                                                                </Link>
                                                            </OverlayTrigger>
                                                        ) : (
                                                            <span className="text-muted small">—</span>
                                                        )}
                                                    </td>
                                                    <td>{equipo?.fechaIngreso ? new Date(equipo.fechaIngreso).toLocaleString() : '-'}</td>
                                                    <td>{equipo?.snapshot?.sede ?? equipo?.sede ?? '-'}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>

                                <Row className="align-items-center">
                                    <Col>
                                        <Form.Select value={pageSize} onChange={(e)=>{setPageSize(Number(e.target.value)); setPage(1);}} style={{width: '120px'}}>
                                            <option value={10}>10</option>
                                            <option value={20}>20</option>
                                            <option value={50}>50</option>
                                        </Form.Select>
                                    </Col>
                                    <Col className="text-end">
                                        <Pagination className="justify-content-end">
                                            <Pagination.First onClick={()=>setPage(1)} disabled={currentPage===1} />
                                            <Pagination.Prev onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={currentPage===1} />
                                            <Pagination.Item active>{currentPage}</Pagination.Item>
                                            <Pagination.Next onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={currentPage===totalPages} />
                                            <Pagination.Last onClick={()=>setPage(totalPages)} disabled={currentPage===totalPages} />
                                        </Pagination>
                                    </Col>
                                </Row>
                            </>
                        )}
                    </>
                )}

                <LoteCreateModal
                    show={showCreateModal}
                    onHide={()=>setShowCreateModal(false)}
                    selectedIds={selectedIds}
                    selectedEquipos={equipos.filter(e=>selectedIds.includes(e._id))}
                    onCreated={() => {
                        setShowCreateModal(false);
                        clearSelection();
                        setRefresh(r => !r);
                        Alertassw('success', 'Lote creado correctamente');
                        onLoteCreado();
                    }}
                    responsable={userData?._id}
                />
            </Container>
        </div>
    );
}
